/* BulkLetters — /admin/blp (Bulk Letter Platform)
   Tab order: Bulk Job (default) → Templates → Job History
   Templates tab shows cards; clicking opens a full-screen detail modal with
   tabs: Preview · JSON · Info & Settings · Merge Fields · Emails — matching old FE. */
import { useState, useRef, useEffect, useCallback } from 'react';
import css from '../AdminPage.module.css';
import Badge from '../../../components/ui/Badge/Badge';
import EmptyState from '../../../components/ui/EmptyState/EmptyState';
import api from '../../../services/api';
import RichEmailEditor from '../../../components/common/RichEmailEditor';
import {
  useBLPTemplates,
  useBLPTemplate,
  useCreateBLPTemplate,
  useDeleteBLPTemplate,
  useBLPFieldDefinitions,
  useSaveBLPFieldMappings,
  useGenerateBLPTest,
  useBLPBulkGenerate,
  useBLPBulkStatus,
  useBLPBulkCaseCount,
  useCancelBLPBulk,
  usePipelines,
  usePipelineStages,
  useUpdateBLPSettings,
  useBLPTemplateEmails,
  useCreateBLPEmail,
  useUpdateBLPEmail,
  useDeleteBLPEmail,
  useBLPJobHistory,
  useFirmLetterhead,
} from '../../../hooks/useAdmin';
import { useToast } from '../../../hooks/useToast';
import DatePicker from '../../../components/ui/DatePicker/DatePicker';
import type { BLPEmailConfig, FieldMappingConfig } from '../../../types/admin';

/* ── Source type options (shared with DraftTemplates) ── */
const SOURCE_TYPES: { value: FieldMappingConfig['source_type']; label: string }[] = [
  { value: 'database', label: 'Database Field' },
  { value: 'system', label: 'System Field' },
  { value: 'custom_sql', label: 'Custom SQL' },
  { value: 'conditional', label: 'Conditional' },
];

const SYSTEM_FIELDS = [
  { value: 'today_date', label: "Today's Date" },
  { value: 'today_date_long', label: "Today's Date (Long)" },
  { value: 'date_plus_7', label: 'Today + 7 days' },
  { value: 'date_plus_14', label: 'Today + 14 days' },
  { value: 'date_plus_28', label: 'Today + 28 days' },
];

const FORMAT_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'title_case', label: 'Title Case' },
  { value: 'upper_case', label: 'UPPER CASE' },
  { value: 'lower_case', label: 'lower case' },
  { value: 'currency', label: 'Currency' },
  { value: 'date_uk', label: 'Date (DD/MM/YYYY)' },
  { value: 'date_long', label: 'Date (Long)' },
];

function normMapping(raw: FieldMappingConfig | string | undefined): FieldMappingConfig {
  if (!raw) return { source_type: 'database' };
  if (typeof raw === 'string') return { source_type: 'database', field_key: raw };
  return { ...raw, source_type: raw.source_type || 'database' };
}

function isMappingConfigured(m: FieldMappingConfig): boolean {
  switch (m.source_type) {
    case 'database':
      return !!(m.field_key || m.field_definition_id);
    case 'system':
      return !!m.system_field;
    case 'custom_sql':
      return !!m.custom_sql?.trim();
    case 'conditional':
      return !!(m.rules && m.rules.length > 0);
    default:
      return false;
  }
}

type Tab = 'bulk' | 'templates' | 'history';

export default function BulkLetters() {
  const { data: templates = [], isLoading } = useBLPTemplates();
  const { data: fieldDefs = [] } = useBLPFieldDefinitions();
  const { data: pipelines = [] } = usePipelines();
  const createTemplate = useCreateBLPTemplate();
  const deleteTemplate = useDeleteBLPTemplate();
  const bulkGenerate = useBLPBulkGenerate();
  const cancelBulk = useCancelBLPBulk();
  const { data: historyData } = useBLPJobHistory();
  const { showToast } = useToast();

  const downloadZip = useCallback(
    async (jobId: string) => {
      try {
        const response = await api.get(`/api/admin/blp-templates/bulk/download/${jobId}`, {
          responseType: 'blob',
        });
        const disposition = String(response.headers['content-disposition'] || '');
        const match = disposition.match(/filename="?([^"]+)"?/);
        const filename = match?.[1] || `bulk-letters-${jobId}.zip`;
        const url = URL.createObjectURL(response.data as Blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch {
        showToast('Failed to download ZIP', 'error');
      }
    },
    [showToast],
  );

  const [tab, setTab] = useState<Tab>('bulk');

  /* ── Upload modal ── */
  const [showUpload, setShowUpload] = useState(false);
  const [tName, setTName] = useState('');
  const [tType, setTType] = useState('general');
  const [tCategory, setTCategory] = useState('Demand');
  const [tFile, setTFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  /* ── Detail modal ── */
  const [detailId, setDetailId] = useState<number | null>(null);

  /* ── Bulk Job state ── */
  const [bulkPipelineId, setBulkPipelineId] = useState<number | null>(null);
  const [bulkStageId, setBulkStageId] = useState<number | null>(null);
  const [bulkTemplateId, setBulkTemplateId] = useState<number | null>(null);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [addToCaseFiles, setAddToCaseFiles] = useState(true);
  const [changeStage, setChangeStage] = useState(false);
  const [targetStageId, setTargetStageId] = useState<number | null>(null);
  const [setLocLba, setSetLocLba] = useState(false);
  const [locLbaDate, setLocLbaDate] = useState(new Date().toISOString().slice(0, 10));
  const [sendEmails, setSendEmails] = useState(false);

  const selectedPipeline = pipelines.find((p) => p.id === bulkPipelineId);
  const caseType = selectedPipeline?.name ?? null;
  const { data: stages = [] } = usePipelineStages(bulkPipelineId);
  const { data: caseCountData } = useBLPBulkCaseCount(caseType, bulkStageId);
  const { data: jobStatus } = useBLPBulkStatus(activeJobId);

  /* ── Handlers ── */
  function openUpload() {
    setTName('');
    setTType('general');
    setTCategory('Demand');
    setTFile(null);
    setShowUpload(true);
  }

  async function handleUpload() {
    const fd = new FormData();
    fd.append('templateName', tName);
    fd.append('templateType', tType);
    fd.append('category', tCategory);
    if (tFile) fd.append('template', tFile);
    try {
      await createTemplate.mutateAsync(fd);
      showToast('Template uploaded', 'success');
      setShowUpload(false);
    } catch {
      showToast('Upload failed', 'error');
    }
  }

  async function handleDelete(e: React.MouseEvent, id: number) {
    e.stopPropagation();
    if (!confirm('Delete this BLP template?')) return;
    try {
      await deleteTemplate.mutateAsync(id);
      showToast('Deleted', 'success');
    } catch {
      showToast('Failed', 'error');
    }
  }

  async function handleBulk() {
    if (!bulkTemplateId || !caseType || !bulkStageId) return;
    try {
      const res = await bulkGenerate.mutateAsync({
        templateId: bulkTemplateId,
        caseType,
        stageId: bulkStageId,
        addToCaseFiles,
        changeStage,
        targetStageId: changeStage && targetStageId ? targetStageId : undefined,
        sendEmails,
        setLBADate: setLocLba,
        lbaDate: setLocLba ? locLbaDate : undefined,
      });
      setActiveJobId(res.job_id ?? res.jobId ?? null);
      showToast('Bulk job started', 'success');
    } catch {
      showToast('Bulk generation failed', 'error');
    }
  }

  if (isLoading) {
    return (
      <div className={css.spinner}>
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
      </div>
    );
  }

  return (
    <>
      <div className={css.section}>
        <div className={css.sectionHeader}>
          <h2 className={css.sectionTitle}>{'\uD83D\uDCEC'} Bulk Letter Platform (BLP)</h2>
        </div>

        {/* Redirect notice — letterhead management moved to Firm Branding */}
        <div
          style={{
            margin: '0 0 12px',
            padding: '10px 14px',
            background: 'var(--info-50, #eff6ff)',
            border: '1px solid var(--info-200, #bfdbfe)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--info-800, #1e40af)',
          }}
        >
          📌 Letterhead is now managed under{' '}
          <a href="/admin/firm-branding" style={{ fontWeight: 600, color: 'inherit' }}>
            Firm Branding → Logos &amp; Letterhead
          </a>
          .
        </div>

        <div className={css.tabs}>
          <button
            className={tab === 'bulk' ? css.tabActive : css.tab}
            onClick={() => setTab('bulk')}
          >
            Bulk Letter Generation
          </button>
          <button
            className={tab === 'templates' ? css.tabActive : css.tab}
            onClick={() => setTab('templates')}
          >
            Letter Templates ({templates.length})
          </button>
          <button
            className={tab === 'history' ? css.tabActive : css.tab}
            onClick={() => setTab('history')}
          >
            Job History{historyData?.total ? ` (${historyData.total})` : ''}
          </button>
        </div>

        {/* ── Bulk Letter Generation ── */}
        {tab === 'bulk' && (
          <div style={{ padding: 16 }}>
            <div className={css.grid2}>
              <div>
                <div className={css.formGroup}>
                  <label className={css.formLabel}>Case Type (Pipeline)</label>
                  <select
                    className={css.formSelect}
                    value={bulkPipelineId ?? ''}
                    onChange={(e) => {
                      setBulkPipelineId(e.target.value ? Number(e.target.value) : null);
                      setBulkStageId(null);
                    }}
                  >
                    <option value="">Select pipeline...</option>
                    {pipelines.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={css.formGroup}>
                  <label className={css.formLabel}>Stage</label>
                  <select
                    className={css.formSelect}
                    value={bulkStageId ?? ''}
                    onChange={(e) => setBulkStageId(e.target.value ? Number(e.target.value) : null)}
                    disabled={!bulkPipelineId}
                  >
                    <option value="">Select stage...</option>
                    {stages.map((s) => (
                      <option key={s.stage_id} value={s.stage_id}>
                        {s.stage_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={css.formGroup}>
                  <label className={css.formLabel}>Template</label>
                  <select
                    className={css.formSelect}
                    value={bulkTemplateId ?? ''}
                    onChange={(e) =>
                      setBulkTemplateId(e.target.value ? Number(e.target.value) : null)
                    }
                  >
                    <option value="">Select template...</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.template_name || t.name}
                      </option>
                    ))}
                  </select>
                </div>
                {caseType && (
                  <div
                    style={{
                      padding: '8px 12px',
                      background: 'var(--primary-50, #eff6ff)',
                      borderRadius: 'var(--radius-md)',
                      marginBottom: 16,
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 600,
                        color: 'var(--primary-700, #1d4ed8)',
                        fontSize: 'var(--font-size-lg)',
                      }}
                    >
                      {caseCountData?.count ?? '...'}
                    </span>
                    <span
                      style={{
                        color: 'var(--primary-600)',
                        marginLeft: 8,
                        fontSize: 'var(--font-size-sm)',
                      }}
                    >
                      cases matching
                    </span>
                  </div>
                )}
                {/* Toggle options */}
                <div
                  style={{
                    border: '1px solid var(--border-primary)',
                    borderRadius: 'var(--radius-md)',
                    padding: 12,
                    marginBottom: 16,
                  }}
                >
                  <label className={css.formLabel} style={{ marginBottom: 12 }}>
                    Generation Options
                  </label>
                  <ToggleRow
                    label="Add to Case Files"
                    checked={addToCaseFiles}
                    onChange={setAddToCaseFiles}
                  />
                  <ToggleRow
                    label="Change Stage After Generation"
                    checked={changeStage}
                    onChange={setChangeStage}
                  />
                  {changeStage && (
                    <select
                      className={css.formSelect}
                      value={targetStageId ?? ''}
                      onChange={(e) =>
                        setTargetStageId(e.target.value ? Number(e.target.value) : null)
                      }
                      style={{ marginLeft: 48, maxWidth: 220, marginBottom: 10 }}
                    >
                      <option value="">Target stage...</option>
                      {stages.map((s) => (
                        <option key={s.stage_id} value={s.stage_id}>
                          {s.stage_name}
                        </option>
                      ))}
                    </select>
                  )}
                  <ToggleRow
                    label="Set LOC/LBA Sent Date"
                    checked={setLocLba}
                    onChange={setSetLocLba}
                  />
                  {setLocLba && (
                    <DatePicker
                      value={locLbaDate}
                      onChange={setLocLbaDate}
                      className={css.formInput}
                      style={{ marginLeft: 48, maxWidth: 180, marginBottom: 10 }}
                    />
                  )}
                  <ToggleRow label="Send Emails" checked={sendEmails} onChange={setSendEmails} />
                </div>
                {!addToCaseFiles && !changeStage && !setLocLba && !sendEmails && (
                  <div
                    style={{
                      padding: '8px 12px',
                      background: '#fffbeb',
                      border: '1px solid #fde68a',
                      borderRadius: 'var(--radius-md)',
                      marginBottom: 12,
                      fontSize: 'var(--font-size-xs)',
                      color: '#b45309',
                    }}
                  >
                    {'\u26A0\uFE0F'} No options enabled — <strong>dry run</strong> (no changes)
                  </div>
                )}
                <button
                  className={css.btnPrimary}
                  onClick={handleBulk}
                  disabled={!bulkTemplateId || !caseType || bulkGenerate.isPending}
                  style={{ width: '100%' }}
                >
                  {bulkGenerate.isPending
                    ? 'Starting...'
                    : `Start Bulk Generation${caseCountData?.count ? ` (${caseCountData.count} cases)` : ''}`}
                </button>
              </div>
              <div>
                <h3 style={{ marginBottom: 12, fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>
                  Job Status
                </h3>
                {!jobStatus ? (
                  <EmptyState message="No active job" />
                ) : (
                  <div
                    style={{
                      padding: 16,
                      border: '1px solid var(--border-primary)',
                      borderRadius: 'var(--radius-md)',
                    }}
                  >
                    <div
                      style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}
                    >
                      <Badge
                        variant={
                          jobStatus.status === 'complete'
                            ? 'success'
                            : jobStatus.status === 'failed'
                              ? 'error'
                              : 'warning'
                        }
                      >
                        {jobStatus.status === 'complete' ? 'completed' : jobStatus.status}
                      </Badge>
                      {(jobStatus.status === 'queued' || jobStatus.status === 'running') && (
                        <button
                          className={`${css.btnDanger} ${css.btnSmall}`}
                          onClick={() => cancelBulk.mutate(activeJobId!)}
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                    <div className={css.progressBar}>
                      <div
                        className={css.progressFill}
                        style={{
                          width: `${jobStatus.total > 0 ? (jobStatus.completed / jobStatus.total) * 100 : 0}%`,
                        }}
                      />
                    </div>
                    <div
                      style={{
                        marginTop: 8,
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--text-tertiary)',
                      }}
                    >
                      {jobStatus.completed} / {jobStatus.total} processed
                      {jobStatus.failed > 0 && (
                        <span style={{ color: 'var(--error-600)' }}>
                          {' '}
                          ({jobStatus.failed} failed)
                        </span>
                      )}
                    </div>
                    {jobStatus.status === 'complete' && activeJobId && (
                      <button
                        className={css.btnPrimary}
                        style={{ marginTop: 12, width: '100%' }}
                        onClick={() => downloadZip(activeJobId)}
                      >
                        Download ZIP
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Templates ── */}
        {tab === 'templates' && (
          <>
            <div
              className={css.toolbar}
              style={{ marginBottom: 16, justifyContent: 'flex-end', padding: '16px 0 0' }}
            >
              <button className={css.btnPrimary} onClick={openUpload}>
                + Upload Template
              </button>
            </div>
            {templates.length === 0 ? (
              <EmptyState icon="📬" message="No BLP templates" />
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: 12,
                }}
              >
                {templates.map((t) => (
                  <div
                    key={t.id}
                    onClick={() => setDetailId(t.id)}
                    style={{
                      border: '1px solid var(--border-primary)',
                      borderRadius: 'var(--radius-lg)',
                      padding: 16,
                      background: 'var(--surface-primary)',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.borderColor = 'var(--primary-500)';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-primary)';
                    }}
                  >
                    <div
                      style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}
                    >
                      <strong
                        style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-primary)' }}
                      >
                        {t.template_name || t.name}
                      </strong>
                      <span
                        style={{
                          background: '#e0f2fe',
                          color: '#0284c7',
                          padding: '2px 8px',
                          borderRadius: 10,
                          fontSize: '0.68rem',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                        }}
                      >
                        {t.template_type || 'general'}
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: '0.78rem',
                        color: 'var(--text-tertiary)',
                        display: 'flex',
                        gap: 8,
                        flexWrap: 'wrap',
                      }}
                    >
                      <span>{t.merge_field_count ?? 0} fields</span>
                      <span>{'\u00B7'}</span>
                      <span>{t.content_block_count ?? 0} blocks</span>
                      <span>{'\u00B7'}</span>
                      <span>{t.category || 'General'}</span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: 10,
                      }}
                    >
                      <span style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>
                        {t.updated_at
                          ? new Date(t.updated_at).toLocaleDateString('en-GB')
                          : '\u2014'}
                      </span>
                      <button
                        onClick={(e) => handleDelete(e, t.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'var(--text-tertiary)',
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                        }}
                        title="Delete"
                      >
                        {'\uD83D\uDDD1\uFE0F'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── Letterhead ── */}
        {/* ── Job History ── */}
        {tab === 'history' && (
          <div style={{ padding: 16 }}>
            {!historyData?.jobs?.length ? (
              <EmptyState message="No completed jobs yet" />
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className={css.table} style={{ minWidth: 800 }}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Template</th>
                      <th>Case Type</th>
                      <th>Status</th>
                      <th>Cases</th>
                      <th>Duration</th>
                      <th>Actions</th>
                      <th style={{ width: 100 }}>Download</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyData.jobs.map((j) => {
                      const created = j.createdAt
                        ? new Date(j.createdAt)
                        : j.created_at
                          ? new Date(j.created_at)
                          : null;
                      const elapsed = j.elapsedMs ? `${Math.round(j.elapsedMs / 1000)}s` : '\u2014';
                      const actions: string[] = [];
                      if (j.s3Uploaded) actions.push(`${j.s3Uploaded} uploaded`);
                      if (j.stagesChanged) actions.push(`${j.stagesChanged} staged`);
                      if (j.guardBlocked) actions.push(`${j.guardBlocked} guard-blocked`);
                      if (j.emailsSent) actions.push(`${j.emailsSent} emailed`);
                      if (j.isDryRun) actions.push('Dry run');
                      return (
                        <tr key={j.id || j.jobId}>
                          <td style={{ whiteSpace: 'nowrap', fontSize: 'var(--font-size-xs)' }}>
                            {created
                              ? created.toLocaleDateString('en-GB') +
                                ' ' +
                                created.toLocaleTimeString('en-GB', {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })
                              : '\u2014'}
                          </td>
                          <td style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>
                            {j.templateName || '\u2014'}
                          </td>
                          <td style={{ fontSize: 'var(--font-size-sm)' }}>
                            {j.caseType || '\u2014'}
                          </td>
                          <td>
                            <Badge
                              variant={
                                j.status === 'complete'
                                  ? 'success'
                                  : j.status === 'failed'
                                    ? 'error'
                                    : 'warning'
                              }
                            >
                              {j.status === 'complete' ? 'completed' : j.status}
                            </Badge>
                          </td>
                          <td style={{ fontSize: 'var(--font-size-sm)' }}>
                            {j.completed}/{j.total}
                            {j.failed > 0 && (
                              <span style={{ color: 'var(--error-600)' }}>
                                {' '}
                                ({j.failed} failed)
                              </span>
                            )}
                          </td>
                          <td style={{ fontSize: 'var(--font-size-xs)' }}>{elapsed}</td>
                          <td
                            style={{
                              fontSize: 'var(--font-size-xs)',
                              color: 'var(--text-tertiary)',
                            }}
                          >
                            {actions.join(', ') || '\u2014'}
                          </td>
                          <td>
                            {j.status === 'complete' && j.zipS3Key ? (
                              <button
                                className={`${css.btnSecondary} ${css.btnSmall}`}
                                onClick={() => downloadZip(j.id || j.jobId!)}
                              >
                                Download
                              </button>
                            ) : (
                              <span
                                style={{
                                  color: 'var(--text-tertiary)',
                                  fontSize: 'var(--font-size-xs)',
                                }}
                              >
                                \u2014
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Upload modal */}
      {showUpload && (
        <div className={css.modalBackdrop} onClick={() => setShowUpload(false)}>
          <div className={css.modal} onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className={css.modalHeader}>
              <h3 className={css.modalTitle}>Upload DOCX Template</h3>
              <button className={css.modalClose} onClick={() => setShowUpload(false)}>
                &times;
              </button>
            </div>
            <div className={css.modalBody}>
              <p
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--text-tertiary)',
                  marginBottom: 12,
                }}
              >
                Upload a DOCX template — it will be auto-parsed into a JSON letter template for PDF
                generation.
              </p>
              <div className={css.grid2}>
                <div className={css.formGroup}>
                  <label className={css.formLabel}>Template Name</label>
                  <input
                    className={css.formInput}
                    value={tName}
                    onChange={(e) => setTName(e.target.value)}
                    placeholder="e.g. Plevin Demand Letter"
                  />
                </div>
                <div className={css.formGroup}>
                  <label className={css.formLabel}>Template Type</label>
                  <select
                    className={css.formSelect}
                    value={tType}
                    onChange={(e) => setTType(e.target.value)}
                  >
                    <option value="general">General</option>
                    <option value="plevin">Plevin</option>
                    <option value="irresponsible_lending">Irresponsible Lending</option>
                    <option value="pcp">PCP</option>
                    <option value="app_scam">App Scam</option>
                  </select>
                </div>
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Category</label>
                <select
                  className={css.formSelect}
                  value={tCategory}
                  onChange={(e) => setTCategory(e.target.value)}
                >
                  <option value="Demand">Demand Letter</option>
                  <option value="DSAR">DSAR Letter</option>
                  <option value="Response">Response Letter</option>
                  <option value="Court">Court Document</option>
                  <option value="General">General</option>
                </select>
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Template File (.docx)</label>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".docx"
                  onChange={(e) => setTFile(e.target.files?.[0] || null)}
                />
              </div>
            </div>
            <div className={css.modalFooter}>
              <button className={css.btnSecondary} onClick={() => setShowUpload(false)}>
                Cancel
              </button>
              <button
                className={css.btnPrimary}
                onClick={handleUpload}
                disabled={!tName || !tFile || createTemplate.isPending}
              >
                {createTemplate.isPending ? 'Converting\u2026' : 'Convert & Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {detailId && (
        <BLPDetailModal
          templateId={detailId}
          fieldDefs={fieldDefs}
          onClose={() => setDetailId(null)}
        />
      )}
    </>
  );
}

/* ────────────────────────────────
 *  Toggle row helper
 * ──────────────────────────────── */
function ToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
      <label style={{ position: 'relative', width: 40, height: 22, display: 'inline-block' }}>
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          style={{ opacity: 0, width: 0, height: 0 }}
        />
        <span
          style={{
            position: 'absolute',
            cursor: 'pointer',
            inset: 0,
            background: checked ? 'var(--primary-600, #2563eb)' : 'var(--text-tertiary, #ccc)',
            borderRadius: 11,
            transition: '0.2s',
          }}
        >
          <span
            style={{
              position: 'absolute',
              height: 18,
              width: 18,
              left: checked ? 20 : 2,
              bottom: 2,
              background: 'white',
              borderRadius: '50%',
              transition: '0.2s',
            }}
          />
        </span>
      </label>
      <span style={{ fontSize: 'var(--font-size-sm)' }}>{label}</span>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 *  BLPDetailModal — Full-screen 5-tab detail view per old FE
 *  Tabs: Preview · JSON · Info & Settings · Merge Fields · Emails
 * ──────────────────────────────────────────────────────────────────── */

type DetailTab = 'preview' | 'json' | 'info' | 'mergefields' | 'emails';

function BLPDetailModal({
  templateId,
  fieldDefs,
  onClose,
}: {
  templateId: number;
  fieldDefs: {
    id?: number;
    name: string;
    key: string;
    description?: string;
    category_name?: string;
    source_table?: string;
  }[];
  onClose: () => void;
}) {
  const { data: template } = useBLPTemplate(templateId);
  const { data: letterhead } = useFirmLetterhead();
  const { data: emails = [] } = useBLPTemplateEmails(templateId);
  // Field mappings are already included in the full template response (field_mappings column).
  // No separate GET endpoint exists — derive from template directly.
  const remoteMappings = (template?.field_mappings ?? {}) as Record<
    string,
    FieldMappingConfig | string
  >;
  const saveMappings = useSaveBLPFieldMappings();
  const generateTest = useGenerateBLPTest();
  const updateSettings = useUpdateBLPSettings();
  const createEmail = useCreateBLPEmail();
  const updateEmail = useUpdateBLPEmail();
  const deleteEmail = useDeleteBLPEmail();
  const { showToast } = useToast();

  const [dtab, setDtab] = useState<DetailTab>('preview');
  const [testCaseRef, setTestCaseRef] = useState('');

  /* ── Info settings state ── */
  const [editName, setEditName] = useState('');
  const [namingConv, setNamingConv] = useState('');
  const [defAddressType, setDefAddressType] = useState('Claim Form');

  /* ── Merge field mapping state ── */
  const [mappings, setMappings] = useState<Record<string, FieldMappingConfig>>({});
  const [mappingsInit, setMappingsInit] = useState(false);
  const [selectedField, setSelectedField] = useState<string | null>(null);

  // Init settings when template loads
  useEffect(() => {
    if (!template) return;
    setEditName(template.template_name || template.name || '');
    setNamingConv(template.naming_convention || '{CaseRef}_{template_name}');
    setDefAddressType(template.defendant_address_type || 'Claim Form');
  }, [template]);

  // Extract merge field placeholders from template_data
  const placeholders = template?.template_data ? extractPlaceholders(template.template_data) : [];

  // Init mappings when remote loads
  useEffect(() => {
    if (mappingsInit) return;
    if (Object.keys(remoteMappings).length > 0 || placeholders.length > 0) {
      const m: Record<string, FieldMappingConfig> = {};
      placeholders.forEach((ph) => {
        m[ph] = normMapping(remoteMappings[ph]);
      });
      Object.entries(remoteMappings).forEach(([k, v]) => {
        if (!m[k]) m[k] = normMapping(v);
      });
      setMappings(m);
      setMappingsInit(true);
    }
  }, [remoteMappings, placeholders, mappingsInit]);

  if (!template) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(0,0,0,0.45)',
        }}
        onClick={onClose}
      >
        <div
          style={{
            width: '95vw',
            maxWidth: 1400,
            height: '90vh',
            borderRadius: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--surface-primary)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ color: 'var(--text-tertiary)' }}>Loading template...</div>
        </div>
      </div>
    );
  }

  const tData = template.template_data || {};
  const fieldNames = Object.keys(mappings);
  const mappedCount = fieldNames.filter((n) => isMappingConfigured(mappings[n])).length;

  function updateMapping(name: string, patch: Partial<FieldMappingConfig>) {
    setMappings((prev) => ({ ...prev, [name]: { ...prev[name], ...patch } }));
  }

  async function handleSaveMappings() {
    try {
      await saveMappings.mutateAsync({
        templateId,
        mappings: mappings as unknown as Record<string, string>,
      });
      showToast('Mappings saved', 'success');
    } catch {
      showToast('Save failed', 'error');
    }
  }

  async function handleSaveSettings() {
    try {
      await updateSettings.mutateAsync({
        templateId,
        template_name: editName,
        naming_convention: namingConv,
        defendant_address_type: defAddressType,
        template_data: tData,
      });
      showToast('Settings saved', 'success');
    } catch {
      showToast('Save failed', 'error');
    }
  }

  async function handleTest() {
    if (!testCaseRef) return;
    try {
      const res = await generateTest.mutateAsync({ templateId, caseRef: testCaseRef });
      const url = URL.createObjectURL(res.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = res.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      showToast('Test generated', 'success');
    } catch {
      showToast('Test failed', 'error');
    }
  }

  async function handleAddEmail() {
    try {
      await createEmail.mutateAsync({
        templateId,
        email_name: 'New Email',
        recipient_type: 'client',
        subject: '',
        body_html: '',
        attach_document: true,
      });
      showToast('Email added', 'success');
    } catch {
      showToast('Failed to add', 'error');
    }
  }

  const TABS: { key: DetailTab; label: string; icon: string }[] = [
    { key: 'preview', label: 'PDF View', icon: '\uD83D\uDCC4' },
    { key: 'json', label: 'JSON View', icon: '\u2726' },
    { key: 'info', label: 'Info & Settings', icon: '\u2699\uFE0F' },
    { key: 'mergefields', label: `Merge Fields (${fieldNames.length})`, icon: '\u21C4' },
    { key: 'emails', label: `Emails (${emails.length})`, icon: '\u2709\uFE0F' },
  ];

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.45)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '95vw',
          maxWidth: 1400,
          height: '90vh',
          borderRadius: 12,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--surface-primary)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 20px',
            borderBottom: '1px solid var(--border-primary)',
            background: 'var(--surface-secondary)',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h3
              style={{
                margin: 0,
                fontSize: 'var(--font-size-base)',
                fontWeight: 700,
                color: 'var(--text-primary)',
              }}
            >
              {template.template_name || template.name}
            </h3>
            <span
              style={{
                background: '#e0f2fe',
                color: '#0284c7',
                padding: '2px 10px',
                borderRadius: 10,
                fontSize: '0.72rem',
                fontWeight: 600,
                textTransform: 'uppercase',
              }}
            >
              {template.template_type || 'general'}
            </span>
            <span
              style={{
                background: '#f0fdf4',
                color: '#16a34a',
                padding: '2px 10px',
                borderRadius: 10,
                fontSize: '0.72rem',
                fontWeight: 500,
              }}
            >
              {template.category || 'General'}
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.4rem',
              cursor: 'pointer',
              color: 'var(--text-tertiary)',
              padding: '0 4px',
              lineHeight: 1,
            }}
            title="Close"
          >
            &times;
          </button>
        </div>

        {/* Tab bar */}
        <div
          style={{
            display: 'flex',
            borderBottom: '2px solid var(--border-primary)',
            flexShrink: 0,
          }}
        >
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setDtab(t.key)}
              style={{
                padding: '10px 20px',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
                border: 'none',
                borderBottom:
                  dtab === t.key ? '2px solid var(--primary-600)' : '2px solid transparent',
                marginBottom: -2,
                background: 'transparent',
                cursor: 'pointer',
                color: dtab === t.key ? 'var(--primary-600)' : 'var(--text-tertiary)',
              }}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
          {/* ── Preview tab ── */}
          {dtab === 'preview' && (
            <div style={{ maxWidth: 700, margin: '0 auto' }}>
              <PdfPreview content={tData.content as unknown[]} letterheadUrl={letterhead?.url} />
              <p
                style={{
                  textAlign: 'center',
                  fontSize: '0.74rem',
                  color: 'var(--text-tertiary)',
                  marginTop: 12,
                }}
              >
                Preview is approximate. Merge fields shown as placeholders.
              </p>
            </div>
          )}

          {/* ── JSON tab ── */}
          {dtab === 'json' && (
            <div style={{ position: 'relative', maxWidth: 900, margin: '0 auto' }}>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(JSON.stringify(tData, null, 2));
                  showToast('Copied', 'success');
                }}
                style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  background: '#334155',
                  border: '1px solid #475569',
                  color: '#e2e8f0',
                  padding: '4px 10px',
                  borderRadius: 4,
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  zIndex: 2,
                }}
              >
                Copy
              </button>
              <pre
                style={{
                  maxHeight: 650,
                  overflow: 'auto',
                  padding: 16,
                  background: '#1e293b',
                  color: '#e2e8f0',
                  borderRadius: 8,
                  fontSize: '0.75rem',
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.5,
                }}
              >
                {JSON.stringify(tData, null, 2)}
              </pre>
            </div>
          )}

          {/* ── Info & Settings tab ── */}
          {dtab === 'info' && (
            <div style={{ maxWidth: 750, margin: '0 auto' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 16,
                }}
              >
                <h4
                  style={{
                    margin: 0,
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--text-primary)',
                  }}
                >
                  {'\u2699\uFE0F'} Template Settings
                </h4>
                <button
                  className={css.btnPrimary}
                  onClick={handleSaveSettings}
                  disabled={updateSettings.isPending}
                  style={{ fontSize: 'var(--font-size-xs)' }}
                >
                  {updateSettings.isPending ? 'Saving\u2026' : 'Save Settings'}
                </button>
              </div>
              {/* Name */}
              <SettingsCard label="Template Name">
                <input
                  className={css.formInput}
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  style={{ fontWeight: 600 }}
                />
              </SettingsCard>
              {/* Naming convention */}
              <SettingsCard label="File Naming Convention">
                <input
                  className={css.formInput}
                  value={namingConv}
                  onChange={(e) => setNamingConv(e.target.value)}
                  style={{ fontFamily: 'monospace' }}
                  placeholder="{CaseRef}_{template_name}"
                />
                <div style={{ marginTop: 6, fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>
                  <strong>Preview:</strong>{' '}
                  <span style={{ color: 'var(--primary-600)', fontFamily: 'monospace' }}>
                    {namingConv || '{CaseRef}_{template_name}'}.pdf
                  </span>
                </div>
                <div
                  style={{
                    marginTop: 8,
                    padding: 8,
                    background: 'var(--surface-secondary)',
                    borderRadius: 6,
                    fontSize: '0.72rem',
                    color: 'var(--text-secondary)',
                  }}
                >
                  Tokens: <code>{'{CaseRef}'}</code> <code>{'{template_name}'}</code>{' '}
                  <code>{'{date}'}</code> <code>{'{date_iso}'}</code> <code>{'{year}'}</code>
                </div>
              </SettingsCard>
              {/* Defendant address type */}
              <SettingsCard label="Defendant Address Category">
                <select
                  className={css.formSelect}
                  value={defAddressType}
                  onChange={(e) => setDefAddressType(e.target.value)}
                >
                  <option value="Claim Form">Claim Form</option>
                  <option value="Service">Service</option>
                  <option value="LoC">Letter of Claim (LoC)</option>
                </select>
                <div style={{ marginTop: 4, fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>
                  Selects which defendant address populates address & email fields during
                  generation.
                </div>
              </SettingsCard>
              {/* Metadata grid */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 12,
                  marginTop: 16,
                }}
              >
                <MetaCard label="Template Type" value={template.template_type || 'general'} />
                <MetaCard label="Category" value={template.category || 'General'} />
                <MetaCard label="Version" value={String(template.version || 1)} />
                <MetaCard
                  label="Created"
                  value={
                    template.created_at
                      ? new Date(template.created_at).toLocaleDateString('en-GB')
                      : 'Unknown'
                  }
                />
                <MetaCard
                  label="Created By"
                  value={
                    template.created_by ||
                    ((tData.metadata as Record<string, unknown>)?.uploadedBy as string) ||
                    'Unknown'
                  }
                />
                <MetaCard
                  label="Content Blocks"
                  value={`${((tData.content as unknown[]) || []).length} blocks`}
                />
              </div>
            </div>
          )}

          {/* ── Merge Fields tab ── */}
          {dtab === 'mergefields' && (
            <div style={{ maxWidth: 900, margin: '0 auto' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 16,
                }}
              >
                <div>
                  <h4
                    style={{
                      margin: 0,
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--text-primary)',
                    }}
                  >
                    {'\u21C4'} Merge Field Mapping
                  </h4>
                  <p
                    style={{
                      fontSize: '0.74rem',
                      color: 'var(--text-tertiary)',
                      margin: '4px 0 0',
                    }}
                  >
                    Map placeholders to Database fields, System values, or Custom SQL expressions.{' '}
                    {mappedCount}/{fieldNames.length} mapped.
                  </p>
                </div>
                <button
                  className={css.btnPrimary}
                  onClick={handleSaveMappings}
                  disabled={saveMappings.isPending}
                  style={{ fontSize: 'var(--font-size-xs)' }}
                >
                  {saveMappings.isPending ? 'Saving\u2026' : 'Save Mappings'}
                </button>
              </div>

              {fieldNames.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-tertiary)' }}>
                  <p>No merge field placeholders detected in this template.</p>
                  <p style={{ fontSize: '0.74rem' }}>
                    Use {'{{field_name}}'} or {'\u00AB'}field_name{'\u00BB'} in your DOCX.
                  </p>
                </div>
              ) : (
                <>
                  <div
                    style={{
                      background: '#eff6ff',
                      border: '1px solid #bfdbfe',
                      borderRadius: 8,
                      padding: '10px 14px',
                      marginBottom: 16,
                      fontSize: '0.78rem',
                      color: '#1e40af',
                    }}
                  >
                    Found <strong>{fieldNames.length}</strong> placeholder
                    {fieldNames.length !== 1 ? 's' : ''}. Choose source type for each.
                  </div>
                  {/* Field table */}
                  <div style={{ overflowX: 'auto' }}>
                    <table
                      style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        border: '1px solid var(--border-primary)',
                        borderRadius: 8,
                        overflow: 'hidden',
                        minWidth: 700,
                      }}
                    >
                      <thead>
                        <tr style={{ background: 'var(--surface-secondary)' }}>
                          <th style={thS}>Placeholder</th>
                          <th style={thS}>Source</th>
                          <th style={thS}>Field / Expression</th>
                          <th style={thS}>Format</th>
                          <th style={{ ...thS, textAlign: 'center' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fieldNames.map((name) => {
                          const m = mappings[name];
                          const mapped = isMappingConfigured(m);
                          const isSelected = selectedField === name;
                          return (
                            <tr
                              key={name}
                              onClick={() => setSelectedField(isSelected ? null : name)}
                              style={{
                                borderBottom: '1px solid var(--border-primary)',
                                cursor: 'pointer',
                                background: isSelected ? 'rgba(139,92,246,0.04)' : undefined,
                              }}
                            >
                              <td style={tdS}>
                                <code
                                  style={{
                                    background: 'var(--surface-tertiary, #f1f5f9)',
                                    padding: '2px 6px',
                                    borderRadius: 4,
                                    fontSize: '0.78rem',
                                    color: '#7c3aed',
                                  }}
                                >
                                  {name}
                                </code>
                              </td>
                              <td style={tdS}>
                                <select
                                  value={m.source_type}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) =>
                                    updateMapping(name, {
                                      source_type: e.target
                                        .value as FieldMappingConfig['source_type'],
                                    })
                                  }
                                  style={smallSelect}
                                >
                                  {SOURCE_TYPES.map((st) => (
                                    <option key={st.value} value={st.value}>
                                      {st.label}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td style={tdS} onClick={(e) => e.stopPropagation()}>
                                {m.source_type === 'database' && (
                                  <select
                                    value={m.field_key || ''}
                                    onChange={(e) => {
                                      const def = fieldDefs.find((d) => d.key === e.target.value);
                                      updateMapping(name, {
                                        field_key: e.target.value,
                                        field_name: def?.name,
                                        field_definition_id: def?.id,
                                        source_table: def?.source_table,
                                      });
                                    }}
                                    style={smallSelect}
                                  >
                                    <option value="">-- Select field --</option>
                                    {fieldDefs.map((d) => (
                                      <option key={d.key} value={d.key}>
                                        {d.name}
                                        {d.category_name ? ` (${d.category_name})` : ''}
                                      </option>
                                    ))}
                                  </select>
                                )}
                                {m.source_type === 'system' && (
                                  <select
                                    value={m.system_field || ''}
                                    onChange={(e) =>
                                      updateMapping(name, { system_field: e.target.value })
                                    }
                                    style={smallSelect}
                                  >
                                    <option value="">-- Select system field --</option>
                                    {SYSTEM_FIELDS.map((sf) => (
                                      <option key={sf.value} value={sf.value}>
                                        {sf.label}
                                      </option>
                                    ))}
                                  </select>
                                )}
                                {m.source_type === 'custom_sql' && (
                                  <textarea
                                    value={m.custom_sql || ''}
                                    onChange={(e) =>
                                      updateMapping(name, { custom_sql: e.target.value })
                                    }
                                    rows={2}
                                    placeholder="e.g. CONCAT(con.FirstName, ' ', con.Surname)"
                                    style={{
                                      width: '100%',
                                      fontSize: '0.72rem',
                                      padding: '4px 6px',
                                      border: '1px solid var(--border-primary)',
                                      borderRadius: 4,
                                      fontFamily: 'monospace',
                                      resize: 'vertical',
                                      boxSizing: 'border-box',
                                      background: 'var(--surface-primary)',
                                      color: 'var(--text-primary)',
                                    }}
                                  />
                                )}
                                {m.source_type === 'conditional' && (
                                  <textarea
                                    value={
                                      m.rules
                                        ? JSON.stringify(
                                            { rules: m.rules, fallback: m.fallback || '' },
                                            null,
                                            2,
                                          )
                                        : ''
                                    }
                                    onChange={(e) => {
                                      try {
                                        const p = JSON.parse(e.target.value);
                                        updateMapping(name, {
                                          rules: p.rules,
                                          fallback: p.fallback,
                                        });
                                      } catch {
                                        /* wait */
                                      }
                                    }}
                                    rows={3}
                                    placeholder='{"rules":[{"conditions":{"col":"val"},"output":"..."}],"fallback":""}'
                                    style={{
                                      width: '100%',
                                      fontSize: '0.7rem',
                                      padding: '4px 6px',
                                      border: '1px solid var(--border-primary)',
                                      borderRadius: 4,
                                      fontFamily: 'monospace',
                                      resize: 'vertical',
                                      boxSizing: 'border-box',
                                      background: 'var(--surface-primary)',
                                      color: 'var(--text-primary)',
                                    }}
                                  />
                                )}
                              </td>
                              <td style={tdS} onClick={(e) => e.stopPropagation()}>
                                <select
                                  value={m.format || 'none'}
                                  onChange={(e) => updateMapping(name, { format: e.target.value })}
                                  style={smallSelect}
                                >
                                  {FORMAT_OPTIONS.map((fo) => (
                                    <option key={fo.value} value={fo.value}>
                                      {fo.label}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td style={{ ...tdS, textAlign: 'center' }}>
                                {mapped ? (
                                  <span style={{ color: '#16a34a', fontSize: '0.75rem' }}>
                                    {'\u2714'} Mapped
                                  </span>
                                ) : (
                                  <span style={{ color: '#f59e0b', fontSize: '0.75rem' }}>
                                    {'\u26A0'} Unmapped
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              {/* Test generation */}
              <div
                style={{
                  marginTop: 24,
                  paddingTop: 20,
                  borderTop: '2px solid var(--border-primary)',
                }}
              >
                <h4 style={{ margin: '0 0 8px', fontSize: 'var(--font-size-sm)' }}>
                  {'\uD83E\uDDEA'} Generate Test Letter
                </h4>
                <p
                  style={{ fontSize: '0.74rem', color: 'var(--text-tertiary)', margin: '0 0 10px' }}
                >
                  Enter a case reference to generate a test PDF with real merged data.
                </p>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <input
                    className={css.formInput}
                    value={testCaseRef}
                    onChange={(e) => setTestCaseRef(e.target.value)}
                    placeholder="e.g. PLEV-354223"
                    style={{ flex: 1, maxWidth: 280 }}
                  />
                  <button
                    className={css.btnPrimary}
                    onClick={handleTest}
                    disabled={!testCaseRef || generateTest.isPending}
                    style={{ fontSize: 'var(--font-size-xs)' }}
                  >
                    {generateTest.isPending ? 'Generating\u2026' : 'Generate & Download'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Emails tab ── */}
          {dtab === 'emails' && (
            <div style={{ maxWidth: 800, margin: '0 auto' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 16,
                }}
              >
                <div>
                  <h4 style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
                    {'\u2709\uFE0F'} Email Configurations
                  </h4>
                  <p
                    style={{
                      fontSize: '0.74rem',
                      color: 'var(--text-tertiary)',
                      margin: '4px 0 0',
                    }}
                  >
                    Configure emails sent when letters are generated.
                  </p>
                </div>
                <button
                  className={css.btnPrimary}
                  onClick={handleAddEmail}
                  style={{ fontSize: 'var(--font-size-xs)' }}
                >
                  + Add Email
                </button>
              </div>
              {/* Token pills */}
              {placeholders.length > 0 && (
                <div
                  style={{
                    background: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    borderRadius: 8,
                    padding: '10px 14px',
                    marginBottom: 16,
                    fontSize: '0.78rem',
                    color: '#1e40af',
                  }}
                >
                  <strong>Merge Tokens:</strong> Use{' '}
                  <code style={{ background: '#dbeafe', padding: '1px 5px', borderRadius: 3 }}>
                    {'{{field_name}}'}
                  </code>{' '}
                  in subject/body.
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                    {placeholders.map((ph) => (
                      <code
                        key={ph}
                        onClick={() => {
                          navigator.clipboard.writeText(`{{${ph}}}`);
                          showToast('Copied', 'success');
                        }}
                        style={{
                          background: '#dbeafe',
                          padding: '2px 8px',
                          borderRadius: 4,
                          cursor: 'pointer',
                          fontSize: '0.72rem',
                          color: '#1e40af',
                        }}
                      >{`{{${ph}}}`}</code>
                    ))}
                  </div>
                </div>
              )}
              {emails.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-tertiary)' }}>
                  <div style={{ fontSize: '2rem', marginBottom: 12 }}>{'\u2709\uFE0F'}</div>
                  <p>No email configurations yet. Click "Add Email" to create one.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {emails.map((cfg) => (
                    <EmailCard
                      key={cfg.id}
                      cfg={cfg}
                      templateId={templateId}
                      onUpdate={async (patch) => {
                        try {
                          await updateEmail.mutateAsync({ templateId, emailId: cfg.id, ...patch });
                        } catch {
                          showToast('Update failed', 'error');
                        }
                      }}
                      onDelete={async () => {
                        if (!confirm('Delete this email config?')) return;
                        try {
                          await deleteEmail.mutateAsync({ templateId, emailId: cfg.id });
                          showToast('Deleted', 'success');
                        } catch {
                          showToast('Delete failed', 'error');
                        }
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Small sub-components ── */

function SettingsCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        background: 'var(--surface-secondary)',
        border: '1px solid var(--border-primary)',
        borderRadius: 8,
        padding: 16,
        marginBottom: 12,
      }}
    >
      <div
        style={{
          fontSize: '0.72rem',
          color: 'var(--text-tertiary)',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function MetaCard({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        background: 'var(--surface-secondary)',
        border: '1px solid var(--border-primary)',
        borderRadius: 8,
        padding: 14,
      }}
    >
      <div
        style={{
          fontSize: '0.72rem',
          color: 'var(--text-tertiary)',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div
        style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--text-primary)' }}
      >
        {value}
      </div>
    </div>
  );
}

function EmailCard({
  cfg,
  templateId: _templateId,
  onUpdate,
  onDelete,
}: {
  cfg: BLPEmailConfig;
  templateId: number;
  onUpdate: (patch: Partial<BLPEmailConfig>) => void;
  onDelete: () => void;
}) {
  return (
    <div
      style={{
        border: '1px solid var(--border-primary)',
        borderRadius: 10,
        padding: 16,
        background: 'var(--surface-primary)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
        }}
      >
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span
            style={{
              background: cfg.recipient_type === 'client' ? '#dbeafe' : '#fef3c7',
              color: cfg.recipient_type === 'client' ? '#1e40af' : '#92400e',
              padding: '3px 10px',
              borderRadius: 12,
              fontSize: '0.72rem',
              fontWeight: 600,
              textTransform: 'uppercase',
            }}
          >
            {cfg.recipient_type}
          </span>
          <input
            value={cfg.email_name}
            onChange={(e) => onUpdate({ email_name: e.target.value })}
            style={{
              border: 'none',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              color: 'var(--text-primary)',
              background: 'transparent',
              outline: 'none',
              borderBottom: '1px dashed var(--border-primary)',
              padding: '2px 4px',
              minWidth: 120,
            }}
          />
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              fontSize: '0.75rem',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={cfg.attach_document}
              onChange={(e) => onUpdate({ attach_document: e.target.checked })}
              style={{ accentColor: '#8b5cf6' }}
            />
            Attach PDF
          </label>
          <button
            onClick={onDelete}
            style={{
              background: 'none',
              border: 'none',
              color: '#ef4444',
              cursor: 'pointer',
              fontSize: '0.85rem',
            }}
            title="Delete"
          >
            {'\uD83D\uDDD1\uFE0F'}
          </button>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
        <div>
          <label
            style={{
              display: 'block',
              fontSize: '0.72rem',
              fontWeight: 600,
              color: 'var(--text-secondary)',
              marginBottom: 3,
            }}
          >
            Recipient
          </label>
          <select
            value={cfg.recipient_type}
            onChange={(e) => onUpdate({ recipient_type: e.target.value as 'client' | 'defendant' })}
            style={{
              padding: '6px 10px',
              border: '1px solid var(--border-primary)',
              borderRadius: 6,
              fontSize: '0.82rem',
              background: 'var(--surface-primary)',
              color: 'var(--text-primary)',
            }}
          >
            <option value="client">Client</option>
            <option value="defendant">Defendant</option>
          </select>
        </div>
      </div>
      <div style={{ marginBottom: 10 }}>
        <label
          style={{
            display: 'block',
            fontSize: '0.72rem',
            fontWeight: 600,
            color: 'var(--text-secondary)',
            marginBottom: 3,
          }}
        >
          Subject
        </label>
        <input
          value={cfg.subject}
          onChange={(e) => onUpdate({ subject: e.target.value })}
          placeholder="e.g. Your case {{CaseRef}} \u2014 update"
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid var(--border-primary)',
            borderRadius: 6,
            fontSize: '0.85rem',
            color: 'var(--text-primary)',
            boxSizing: 'border-box',
            background: 'var(--surface-primary)',
          }}
        />
      </div>
      <div>
        <label
          style={{
            display: 'block',
            fontSize: '0.72rem',
            fontWeight: 600,
            color: 'var(--text-secondary)',
            marginBottom: 6,
          }}
        >
          Email Body
        </label>
        <RichEmailEditor
          value={cfg.body_html || ''}
          onChange={(html) => onUpdate({ body_html: html })}
          placeholder="Type your email message here..."
          height={250}
        />
      </div>
    </div>
  );
}

/* ── PDF Preview renderer (pdfmake JSON → HTML) with A4 page splitting & letterhead ── */

const PAGE_W = 595;
const PAGE_H = 842;
const PAD_TOP_FIRST = 100;
const PAD_TOP_REST = 60;
const PAD_BOTTOM_FIRST = 80;
const PAD_BOTTOM_REST = 60;
const PAD_LR = 60;
const CONTENT_W = PAGE_W - 2 * PAD_LR;

function renderSegment(seg: unknown): string {
  if (typeof seg === 'string') return escapeHtml(seg);
  const s = seg as Record<string, unknown>;
  let h = escapeHtml(String(s.text || ''));
  if (s.bold) h = '<strong>' + h + '</strong>';
  if (s.italics) h = '<em>' + h + '</em>';
  if (s.decoration === 'underline') h = '<u>' + h + '</u>';
  return h;
}

function renderBlock(block: unknown): string {
  const b = block as Record<string, unknown>;
  if (b.ul || b.ol) {
    const tag = b.ol ? 'ol' : 'ul';
    const items = (b.ol || b.ul) as unknown[];
    return (
      '<' +
      tag +
      ' style="margin:4px 0 8px 20px;font-size:11px;line-height:1.5;font-family:serif;">' +
      items
        .map((item) => {
          if (typeof item === 'string') return '<li>' + escapeHtml(item) + '</li>';
          const i = item as Record<string, unknown>;
          if (i.text !== undefined)
            return (
              '<li>' +
              (Array.isArray(i.text)
                ? (i.text as unknown[]).map(renderSegment).join('')
                : escapeHtml(String(i.text))) +
              '</li>'
            );
          return '<li></li>';
        })
        .join('') +
      '</' +
      tag +
      '>'
    );
  }
  if (!b.text && !b.style) return '<div style="height:8px;"></div>';
  let fontSize = '11px',
    fontWeight = 'normal',
    marginBottom = '6px',
    textAlign = 'left';
  if (b.style === 'heading1') {
    fontSize = '16px';
    fontWeight = 'bold';
    marginBottom = '10px';
  } else if (b.style === 'heading2') {
    fontSize = '14px';
    fontWeight = 'bold';
    marginBottom = '8px';
  } else if (b.style === 'heading3') {
    fontSize = '12px';
    fontWeight = 'bold';
  } else if (b.style === 'centered') {
    textAlign = 'center';
  }
  if (b.bold) fontWeight = 'bold';
  const inner = Array.isArray(b.text)
    ? (b.text as unknown[]).map(renderSegment).join('')
    : escapeHtml(String(b.text || ''));
  return `<div style="font-size:${fontSize};font-weight:${fontWeight};text-align:${textAlign};margin-bottom:${marginBottom};line-height:1.5;font-family:'Times New Roman',serif;">${inner}</div>`;
}

interface PageData {
  blocks: string[];
  padTop: number;
  padBottom: number;
}

function PdfPreview({ content, letterheadUrl }: { content?: unknown[]; letterheadUrl?: string }) {
  const [pages, setPages] = useState<PageData[]>([]);
  const measurerRef = useRef<HTMLDivElement>(null);

  const contentHtml =
    content && content.length > 0
      ? content.map(renderBlock).join('')
      : '<p style="color:#94a3b8;font-style:italic;">No content blocks in this template.</p>';

  useEffect(() => {
    // Off-screen measurer
    const measurer = document.createElement('div');
    measurer.style.cssText = [
      'position:absolute',
      'left:-9999px',
      'top:0',
      `width:${CONTENT_W}px`,
      'font-family:"Times New Roman",serif',
      'font-size:11px',
      'line-height:1.5',
      'visibility:hidden',
    ].join(';');
    measurer.innerHTML = contentHtml;
    document.body.appendChild(measurer);
    // Force layout
    void measurer.offsetHeight;

    const children = Array.from(measurer.children) as HTMLElement[];
    const result: PageData[] = [];
    let curBlocks: string[] = [];
    let curH = 0;
    let pageIdx = 0;

    function padTop() {
      return pageIdx === 0 ? PAD_TOP_FIRST : PAD_TOP_REST;
    }
    function padBot() {
      return pageIdx === 0 ? PAD_BOTTOM_FIRST : PAD_BOTTOM_REST;
    }
    function maxH() {
      return PAGE_H - padTop() - padBot();
    }

    function measureEl(el: HTMLElement) {
      const r = el.getBoundingClientRect();
      const s = getComputedStyle(el);
      return r.height + (parseFloat(s.marginTop) || 0) + (parseFloat(s.marginBottom) || 0);
    }

    function finishPage() {
      result.push({ blocks: curBlocks, padTop: padTop(), padBottom: padBot() });
      pageIdx++;
      curBlocks = [];
      curH = 0;
    }

    function remeasure(htmlStr: string) {
      const tmp = document.createElement('div');
      tmp.style.cssText = measurer.style.cssText;
      tmp.innerHTML = htmlStr;
      document.body.appendChild(tmp);
      void tmp.offsetHeight;
      const h = tmp.children[0] ? measureEl(tmp.children[0] as HTMLElement) : 0;
      document.body.removeChild(tmp);
      return h;
    }

    for (let ci = 0; ci < children.length; ci++) {
      const child = children[ci];
      const h = measureEl(child);
      const remaining = maxH() - curH;
      const tag = (child.tagName || '').toLowerCase();
      const isList = tag === 'ul' || tag === 'ol';

      if (h <= remaining) {
        curBlocks.push(child.outerHTML);
        curH += h;
        continue;
      }

      // Try to split lists
      if (isList && remaining > 40) {
        const lis = Array.from(child.children).filter(
          (c) => (c.tagName || '').toLowerCase() === 'li',
        ) as HTMLElement[];
        if (lis.length > 1) {
          let usedH = 0,
            splitIdx = 0;
          for (let li = 0; li < lis.length; li++) {
            const liH = measureEl(lis[li]);
            if (usedH + liH > remaining && splitIdx > 0) break;
            usedH += liH;
            splitIdx = li + 1;
          }
          if (splitIdx > 0 && splitIdx < lis.length) {
            const style = child.getAttribute('style') || '';
            let firstHalf = `<${tag} style="${style}">`;
            for (let k = 0; k < splitIdx; k++) firstHalf += lis[k].outerHTML;
            firstHalf += `</${tag}>`;
            let secondHalf = `<${tag} style="${style}">`;
            for (let k = splitIdx; k < lis.length; k++) secondHalf += lis[k].outerHTML;
            secondHalf += `</${tag}>`;
            curBlocks.push(firstHalf);
            finishPage();
            curBlocks.push(secondHalf);
            curH = remeasure(secondHalf);
            continue;
          }
        }
      }

      // Push whole block to next page
      if (curBlocks.length > 0) finishPage();
      curBlocks.push(child.outerHTML);
      curH = h;
    }

    // Last page
    if (curBlocks.length > 0) {
      result.push({ blocks: curBlocks, padTop: padTop(), padBottom: padBot() });
    }

    document.body.removeChild(measurer);

    if (result.length === 0) {
      result.push({
        blocks: ['<p style="color:#94a3b8;font-style:italic;">No content blocks.</p>'],
        padTop: PAD_TOP_FIRST,
        padBottom: PAD_BOTTOM_FIRST,
      });
    }

    setPages(result);
  }, [contentHtml]);

  if (pages.length === 0) return null;

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 24, alignItems: 'center' }}
      ref={measurerRef}
    >
      {pages.map((page, idx) => {
        const contentMaxH = PAGE_H - page.padTop - page.padBottom;
        return (
          <div
            key={idx}
            style={{
              width: PAGE_W,
              height: PAGE_H,
              background: '#fff',
              border: '1px solid #cbd5e1',
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Letterhead background on every page */}
            {letterheadUrl && (
              <iframe
                src={letterheadUrl + '#toolbar=0&navpanes=0&scrollbar=0'}
                title="letterhead"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  pointerEvents: 'none',
                  zIndex: 0,
                }}
              />
            )}
            {/* Content layer */}
            <div
              style={{
                position: 'absolute',
                top: page.padTop,
                left: PAD_LR,
                right: PAD_LR,
                maxHeight: contentMaxH,
                overflow: 'hidden',
                zIndex: 1,
                color: '#1e293b',
              }}
              dangerouslySetInnerHTML={{ __html: page.blocks.join('') }}
            />
            {/* Page number */}
            <div
              style={{
                position: 'absolute',
                bottom: 6,
                right: 12,
                fontSize: 9,
                color: '#94a3b8',
                zIndex: 2,
              }}
            >
              Page {idx + 1} of {pages.length}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Helpers ── */

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Extract <<placeholder>> and {{placeholder}} names from pdfmake template_data */
function extractPlaceholders(tData: Record<string, unknown>): string[] {
  const set = new Set<string>();
  const json = JSON.stringify(tData);
  // Match <<name>> and {{name}} — allow spaces (e.g. {{First Name}}, {{Date of Birth }})
  const re = /(?:<<|{{)\s*([\w][\w .'()-]*?)\s*(?:>>|}})/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(json)) !== null) set.add(m[1].trim());
  // Also merge from mergeFields array if present (objects {name,label,type} or plain strings)
  const mf = tData.mergeFields;
  if (Array.isArray(mf))
    mf.forEach((f: unknown) => {
      if (typeof f === 'string') set.add(f);
      else if (f && typeof f === 'object' && 'name' in (f as Record<string, unknown>))
        set.add((f as Record<string, string>).name);
    });
  return Array.from(set).sort();
}

/* Shared inline styles */
const thS: React.CSSProperties = {
  padding: '8px 10px',
  textAlign: 'left',
  fontSize: '0.72rem',
  color: 'var(--text-tertiary)',
  fontWeight: 600,
};
const tdS: React.CSSProperties = {
  padding: '8px 10px',
  verticalAlign: 'middle',
  fontSize: '0.82rem',
};
const smallSelect: React.CSSProperties = {
  width: '100%',
  fontSize: '0.75rem',
  padding: '4px 6px',
  border: '1px solid var(--border-primary)',
  borderRadius: 4,
  background: 'var(--surface-primary)',
  color: 'var(--text-primary)',
};
